import React, { useEffect, useState } from "react";
import { FormControl, Modal, Table } from "react-bootstrap";
import Button from "../components/SurveyButton";
import SurveyConfirmationModal from "../components/SurveyConfirmationModal";
import { deploySurveyTest, removeSurveyTestDeployment } from "api/survey/surveyApi";
import { apiErrorMessage } from "api/survey/surveyApi.utils";
import useTestLeadSearch from "./useTestLeadSearch";

const SurveyTestDeploymentModal = ({ show, surveyId, onHide, onDeployed, onAssignmentRemoved }) => {
  const { search, changeSearch, leads, assignedLeads, loading, error, setError, loadLeads, activeSearchRef } = useTestLeadSearch({ show, surveyId });
  const [selected, setSelected] = useState({});
  const [deploying, setDeploying] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [removalLead, setRemovalLead] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!show) return undefined;
    setSelected({});
    setConfirming(false);
    setRemovalLead(null);
    setSuccess("");
    return undefined;
  }, [show]);

  const toggleLead = (lead) => {
    if (lead.assigned) return;
    setSelected((current) => {
      const next = { ...current };
      if (next[lead.id]) delete next[lead.id];
      else next[lead.id] = lead;
      return next;
    });
  };

  const selectedLeads = Object.keys(selected).map((id) => selected[id]);

  const deploy = async () => {
    if (!selectedLeads.length || deploying) return;
    setDeploying(true);
    setError("");
    setSuccess("");
    try {
      const response = await deploySurveyTest(surveyId, selectedLeads.map((lead) => lead.id));
      setConfirming(false);
      const assignedCount = Number(response.data.assignedCount || 0);
      setSelected({});
      await loadLeads(leads.currentPage, activeSearchRef.current);
      setSuccess(assignedCount
        ? `${assignedCount} lead${assignedCount === 1 ? "" : "s"} deployed for testing.`
        : "All selected leads were already deployed for testing.");
      onDeployed(response.data);
    } catch (requestError) {
      setError(apiErrorMessage(requestError, "Unable to deploy the survey for testing."));
      setConfirming(false);
    }
    setDeploying(false);
  };

  const removeDeployment = async () => {
    if (!removalLead || removing) return;
    setRemoving(true);
    setError("");
    setSuccess("");
    try {
      const response = await removeSurveyTestDeployment(surveyId, removalLead.id);
      const removedLead = removalLead;
      setRemovalLead(null);
      await loadLeads(leads.currentPage, activeSearchRef.current);
      setSuccess(`${removedLead.name || `Lead #${removedLead.id}`} no longer has test access.`);
      if (onAssignmentRemoved) onAssignmentRemoved(response.data);
    } catch (requestError) {
      setError(apiErrorMessage(requestError, "Unable to remove the test deployment."));
      setRemovalLead(null);
    }
    setRemoving(false);
  };

  const busy = deploying || removing;

  return (
    <>
      <Modal className="survey-test-deployment-modal" show={show} onHide={busy ? () => {} : onHide} backdrop={busy ? "static" : true} keyboard={!busy}>
        <Modal.Header closeButton={!busy}>
          <Modal.Title>Deploy Survey for Testing</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="survey-test-deployment-note">
            <i className="fa fa-info-circle" />
            <span>Selected leads can repeatedly test the saved draft. Targeting is bypassed, rewards are disabled, and answers are not stored.</span>
          </div>
          <div className="survey-test-current-leads">
            <div className="survey-test-current-leads-heading">
              <div><strong>Testing leads</strong><span>Leads currently able to access this draft</span></div>
              <span>{assignedLeads.length} deployed · {selectedLeads.length} selected to add</span>
            </div>
            {assignedLeads.length > 0 ? (
              <div className="survey-test-assigned-leads-list">
                {assignedLeads.map((lead) => (
                  <div className="survey-test-assigned-lead" key={lead.id}>
                    <div><strong>{lead.name || "Unnamed lead"}</strong><span>Lead #{lead.id} · {lead.mobileNumber || "No phone number"}</span></div>
                    <Button bsSize="small" bsStyle="danger" icon="fa-trash" disabled={busy} onClick={() => setRemovalLead(lead)}>Remove</Button>
                  </div>
                ))}
              </div>
            ) : <p className="survey-test-no-assigned-leads">No leads are currently deployed for testing.</p>}
            {selectedLeads.length > 0 && (
              <div className="survey-test-pending-leads">
                <strong>Selected to add</strong>
                <div className="survey-test-selected-leads">
                  {selectedLeads.map((lead) => (
                    <button type="button" key={lead.id} disabled={busy} onClick={() => toggleLead(lead)}>
                      {lead.name || "Unnamed lead"} <span>×</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <FormControl
            autoFocus
            value={search}
            disabled={busy}
            placeholder="Search leads by name or phone number"
            onChange={(event) => changeSearch(event.target.value)}
          />
          <div className="survey-test-deployment-summary">
            <strong>Select additional leads</strong>
            {leads.total > 0 && <span>Showing {leads.from}–{leads.to} of {leads.total}</span>}
          </div>
          {error && <div className="survey-engine-validation-banner">{error}</div>}
          {success && <div className="survey-test-deployment-success">{success}</div>}
          <div className="survey-test-lead-table-wrap">
            <Table responsive hover className="survey-engine-table survey-test-lead-table">
              <thead><tr><th>Select</th><th>Lead ID</th><th>Name</th><th>Phone number</th><th>Status</th></tr></thead>
              <tbody>
                {loading && <tr><td colSpan="5" className="survey-engine-empty">Loading leads…</td></tr>}
                {!loading && !leads.rows.length && <tr><td colSpan="5" className="survey-engine-empty">No leads found.</td></tr>}
                {!loading && leads.rows.map((lead) => (
                  <tr className={lead.assigned ? "assigned" : ""} key={lead.id} onClick={() => toggleLead(lead)}>
                    <td><input type="checkbox" checked={Boolean(lead.assigned || selected[lead.id])} disabled={lead.assigned || busy} onChange={() => toggleLead(lead)} onClick={(event) => event.stopPropagation()} /></td>
                    <td>{lead.id}</td>
                    <td>{lead.name || "—"}</td>
                    <td>{lead.mobileNumber || "—"}</td>
                    <td>{lead.assigned ? <span className="survey-test-assigned-label">Assigned</span> : "Available"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          {leads.lastPage > 1 && (
            <div className="survey-engine-pager survey-test-lead-pager">
              <Button icon="fa-chevron-left" disabled={loading || leads.currentPage <= 1} onClick={() => loadLeads(leads.currentPage - 1)}>Previous</Button>
              <strong>Page {leads.currentPage} of {leads.lastPage}</strong>
              <Button icon="fa-chevron-right" disabled={loading || leads.currentPage >= leads.lastPage} onClick={() => loadLeads(leads.currentPage + 1)}>Next</Button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button icon="fa-times" disabled={busy} onClick={onHide}>Close</Button>
          <Button bsStyle="primary" icon="fa-flask" disabled={busy || !selectedLeads.length} onClick={() => setConfirming(true)}>Deploy to {selectedLeads.length || 0} lead{selectedLeads.length === 1 ? "" : "s"}</Button>
        </Modal.Footer>
      </Modal>
      <SurveyConfirmationModal
        show={confirming}
        title="Deploy Test Survey?"
        message={`Deploy this saved draft to ${selectedLeads.length} selected lead${selectedLeads.length === 1 ? "" : "s"}? They can complete it repeatedly without receiving a reward.`}
        confirmLabel="Deploy Test"
        confirmStyle="primary"
        busy={deploying}
        onCancel={() => setConfirming(false)}
        onConfirm={deploy}
      />
      <SurveyConfirmationModal
        show={Boolean(removalLead)}
        title="Remove Test Deployment?"
        message={removalLead ? `Remove test access for ${removalLead.name || `Lead #${removalLead.id}`}? They will immediately stop seeing this draft survey.` : ""}
        confirmLabel="Remove Access"
        confirmStyle="danger"
        busy={removing}
        onCancel={() => setRemovalLead(null)}
        onConfirm={removeDeployment}
      />
    </>
  );
};

export default SurveyTestDeploymentModal;
